import { useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { LockSimpleIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import type { TaskProcedureLock } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { toast } from 'src/components/ui/toast';
import { useCan } from 'src/hooks/useCan';
import {
  useReleaseTaskProcedureLock,
  useTaskPlaybook,
} from 'src/api/rpc/hooks/tasks/useTaskPlaybook';

/**
 * The procedure versions this task locked on first activation. Every retry
 * reuses the locked version, so a newer publication is ignored until someone
 * chooses "Use latest version". Renders nothing when nothing is locked.
 */
export function TaskProcedureLocksCard({ taskId }: { taskId: string }) {
  const gt = useGT();
  const { data: playbook } = useTaskPlaybook(taskId);
  const [canUpdateTasks] = useCan(Action.Update, Subject.Task);
  const release = useReleaseTaskProcedureLock(taskId);

  async function handleRelease(sopId: string) {
    try {
      await release.mutateAsync(sopId);
      toast.success(
        gt('The next attempt will use the latest procedure version', {
          context: 'toast after releasing a task procedure version lock',
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not switch to the latest procedure version', {
              context: 'toast after releasing a task procedure lock failed',
            }),
      );
    }
  }

  return (
    <TaskProcedureLocksCardView
      locks={playbook?.procedureLocks ?? []}
      canRelease={canUpdateTasks}
      pendingSopId={release.isPending ? (release.variables ?? null) : null}
      onRelease={handleRelease}
    />
  );
}

/** Display body, split out so tests can render assembled data directly. */
export function TaskProcedureLocksCardView({
  locks,
  canRelease,
  pendingSopId,
  onRelease,
}: {
  locks: TaskProcedureLock[];
  canRelease: boolean;
  pendingSopId: string | null;
  onRelease: (sopId: string) => void | Promise<void>;
}) {
  const [confirming, setConfirming] = useState<TaskProcedureLock | null>(null);
  if (locks.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-1.5 rounded-md border border-border bg-white px-3 py-2"
      data-testid="task-procedure-locks"
    >
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        <LockSimpleIcon className="size-3.5" />
        <T context="heading for the procedure versions a task is locked to">
          Procedure versions
        </T>
      </span>
      {locks.map((lock) => {
        const outdated =
          lock.latestVersion !== null &&
          lock.latestVersion !== lock.lockedVersion;
        return (
          <div
            key={lock.sopId}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone-700"
          >
            <span className="font-medium">{lock.name}</span>
            <span
              className={outdated ? 'text-amber-700' : 'text-muted-foreground'}
              data-testid={`procedure-lock-${lock.sopId}`}
            >
              {lock.latestVersion === null ? (
                <T context="task procedure lock: the version the task keeps using">
                  Locked to v<Var>{lock.lockedVersion}</Var>
                </T>
              ) : (
                <T context="task procedure lock: locked version and the procedure's current version">
                  Locked to v<Var>{lock.lockedVersion}</Var> · latest v
                  <Var>{lock.latestVersion}</Var>
                </T>
              )}
            </span>
            {outdated && canRelease && (
              <Button
                size="sm"
                variant="outline"
                disabled={pendingSopId === lock.sopId}
                onClick={() => setConfirming(lock)}
              >
                <T context="button: make the task's next attempt use the procedure's latest version">
                  Use latest version
                </T>
              </Button>
            )}
          </div>
        );
      })}
      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="confirm dialog title: switch a task to the latest procedure version">
                Use the latest procedure version?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="confirm dialog body: releasing a task's procedure version lock">
                <Var>{confirming?.name}</Var> is locked to v
                <Var>{confirming?.lockedVersion}</Var> for this task. The next
                attempt will use v<Var>{confirming?.latestVersion}</Var> and its
                session rules. Earlier attempts are unchanged.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <T context="dialog cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirming) void onRelease(confirming.sopId);
                setConfirming(null);
              }}
            >
              <T context="confirm button: switch the task to the latest procedure version">
                Use v<Var>{confirming?.latestVersion}</Var>
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
